/**
 * Browser requests to the federated graph.
 *
 * The page hands over its configuration in an element the server renders only
 * for controllers that acquire a router access token; the token itself arrives
 * in an inert JSON block that this module parses once and removes from the DOM,
 * so it survives only in memory here.
 */

const CONFIG_ELEMENT_ID = 'federated-graphql';
const TOKEN_BLOCK_SELECTOR = '[data-federated-graphql-token]';

/**
 * The prefix every operation name sent from this application must carry. The
 * gateway enforces `<product><OperationDescription>` in camelCase, so build
 * names from this constant rather than writing them out.
 */
export const PRODUCT = 'businessPortal';

export interface FederatedGraphqlConfig {
  url: string;
  clientName: string;
  clientVersion: string;
  token: string;
}

export interface GraphqlResponseError {
  message: string;
  path?: (string | number)[];
  extensions?: Record<string, unknown>;
}

/** `status` is null when the request never reached the router. */
export interface FederatedGraphqlError extends Error {
  status: number | null;
  errors: GraphqlResponseError[];
}

export interface FederatedGraphqlRequest<V> {
  operationName: string;
  query: string;
  variables?: V;
}

interface GraphqlResponseBody<T> {
  data?: T;
  errors?: GraphqlResponseError[];
}

// Tagging an Error rather than subclassing it: this bundle compiles to ES5,
// where `instanceof` on a subclass of a built-in does not hold.
function federatedGraphqlError(
  message: string,
  status: number | null,
  errors: GraphqlResponseError[] = [],
): FederatedGraphqlError {
  const error = new Error(message) as FederatedGraphqlError;
  error.status = status;
  error.errors = errors;
  return error;
}

let cachedConfig: FederatedGraphqlConfig | null = null;

function requiredAttribute(element: Element, name: string): string {
  const value = element.getAttribute(name);
  // The server renders all of these together or none of them, so a missing one
  // means the page is mis-rendered. Failing here beats sending the router a
  // request it cannot attribute to a client.
  if (!value) throw federatedGraphqlError(`Page is missing ${name}.`, null);

  return value;
}

function readToken(element: Element): string {
  const block = element.querySelector(TOKEN_BLOCK_SELECTOR);
  if (!block) return '';

  let token = '';
  try {
    token = (JSON.parse(block.textContent || '{}') as { token?: string }).token || '';
  } catch {
    token = '';
  }
  // Out of the DOM before anything can serialize it: the page keeps no copy,
  // and neither does a Turbolinks snapshot taken on the way to the next page.
  block.remove();

  return token;
}

/**
 * Reads the page's federated graph configuration, consuming the token block.
 *
 * Returns null on a page that carries no configuration. A page that carries one
 * is read exactly once; later calls -- and calls made against a restored
 * Turbolinks preview, which has no token block of its own -- reuse what that
 * read found. A fresh block always wins, so navigating to another page picks up
 * that page's token rather than pinning the first one.
 */
export function readFederatedGraphqlConfig(): FederatedGraphqlConfig | null {
  const element = document.getElementById(CONFIG_ELEMENT_ID);
  if (!element) return cachedConfig;

  const token = readToken(element);
  if (!token) return cachedConfig;

  cachedConfig = {
    url: requiredAttribute(element, 'data-graphql-url'),
    clientName: requiredAttribute(element, 'data-graphql-client-name'),
    clientVersion: requiredAttribute(element, 'data-graphql-client-version'),
    token,
  };

  return cachedConfig;
}

/**
 * Consumes each page's token block as soon as Turbolinks renders it, so the
 * block's removal from the DOM does not depend on a controller getting around
 * to a request before the next navigation. `turbolinks:load` fires on the
 * initial load and on every visit -- including cache restores, whose snapshot
 * would otherwise carry an unconsumed block back into the live DOM.
 */
export function startFederatedGraphqlConfigConsumption(): void {
  document.addEventListener('turbolinks:load', () => {
    readFederatedGraphqlConfig();
  });
}

function assertNamedOperation(operationName: string, query: string): void {
  if (operationName.indexOf(PRODUCT) !== 0) {
    throw federatedGraphqlError(`Operation "${operationName}" must start with "${PRODUCT}".`, null);
  }
  // The gateway rejects an anonymous operation, and an operationName that the
  // document does not declare is anonymous as far as it is concerned.
  if (query.indexOf(operationName) === -1) {
    throw federatedGraphqlError(`Query does not declare the operation "${operationName}".`, null);
  }
}

async function readBody<T>(response: Response): Promise<GraphqlResponseBody<T>> {
  try {
    return await (response.json() as Promise<GraphqlResponseBody<T>>);
  } catch {
    // A gateway or proxy error is HTML, not GraphQL.
    return {};
  }
}

/**
 * Sends one named operation to the federated graph and returns its data.
 *
 * Throws a FederatedGraphqlError for every failure -- unreachable router,
 * non-2xx response, or a response carrying errors. Errors alongside partial
 * data count as failure: nothing here renders a half-populated view.
 */
export async function federatedGraphql<T, V = Record<string, unknown>>({
  operationName,
  query,
  variables,
}: FederatedGraphqlRequest<V>): Promise<T> {
  const config = readFederatedGraphqlConfig();
  if (!config) throw federatedGraphqlError('This page cannot reach the Odeko API.', null);

  assertNamedOperation(operationName, query);

  let response: Response;
  try {
    response = await fetch(config.url, {
      method: 'POST',
      // The router is a different origin and authorizes off the bearer token
      // alone, so sending no cookies keeps the preflight simple.
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.token}`,
        'apollographql-client-name': config.clientName,
        'apollographql-client-version': config.clientVersion,
      },
      body: JSON.stringify({ operationName, query, variables: variables || {} }),
    });
  } catch {
    throw federatedGraphqlError('Could not reach the Odeko API.', null);
  }

  const body = await readBody<T>(response);
  const errors = body.errors || [];

  if (errors.length > 0) {
    throw federatedGraphqlError(errors[0].message, response.status, errors);
  }
  if (!response.ok) {
    throw federatedGraphqlError(`The Odeko API returned ${response.status}.`, response.status);
  }
  if (!body.data) {
    throw federatedGraphqlError('The Odeko API returned no data.', response.status);
  }

  return body.data;
}
